import { db } from "./db";

const META_KEY = "lexibit-meta";

/** kv keys for backup-reminder bookkeeping */
export const KV_LAST_EXPORT_AT = "backup:lastExportAt";
export const KV_LAST_REMINDER_AT = "backup:lastReminderAt";

const TABLES = [
  "concepts",
  "conceptForms",
  "cardStates",
  "decks",
  "activity",
  "kv",
] as const;

type TableName = (typeof TABLES)[number];

export type BackupShape = {
  version: 1;
  exportedAt: number;
  meta: unknown;
  dexie: Record<TableName, unknown[]>;
};

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function timestampForFilename(d: Date): string {
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}`
  );
}

export async function exportBackup(): Promise<{ blob: Blob; filename: string }> {
  const dexie = {} as Record<TableName, unknown[]>;
  for (const name of TABLES) {
    dexie[name] = await db.table(name).toArray();
  }

  let meta: unknown = null;
  const raw = localStorage.getItem(META_KEY);
  if (raw) {
    try {
      meta = JSON.parse(raw);
    } catch {
      meta = null;
    }
  }

  const now = Date.now();
  const payload: BackupShape = {
    version: 1,
    exportedAt: now,
    meta,
    dexie,
  };

  // Record timestamp so the backup reminder knows when we last exported.
  await db.kv.put({ id: KV_LAST_EXPORT_AT, value: now });

  const blob = new Blob([JSON.stringify(payload)], {
    type: "application/json",
  });
  const filename = `lexibit-backup-${timestampForFilename(new Date())}.json`;
  return { blob, filename };
}

/** Parameters for {@link shouldRemindBackup}. All timestamps are Unix ms or null. */
export type BackupReminderParams = {
  /** When was the last export done? null = never */
  lastExportAt: number | null;
  /** When was the last reminder toast shown? null = never */
  lastReminderAt: number | null;
  /** Is the user cloud-linked (has remote backup)? */
  cloudLinked: boolean;
  /** Number of cardStates in the DB (proxy for "has meaningful data") */
  cardStateCount: number;
  /** Number of decks in the DB */
  deckCount: number;
  /** When was the first deck created? Used as "data age". null = no data */
  oldestDeckCreatedAt: number | null;
  /** Current time in ms (injectable for tests) */
  now: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const REMIND_AFTER_MS = 14 * DAY_MS;  // remind if no export in 14 days
const SNOOZE_MS = 7 * DAY_MS;         // don't re-show for 7 days
const MIN_CARD_STATES = 10;           // meaningful-data threshold

/**
 * Pure function: returns true when a backup reminder should be shown.
 *
 * Conditions (all must hold):
 * 1. User is NOT cloud-linked.
 * 2. User has meaningful data (≥ MIN_CARD_STATES OR ≥ 1 deck).
 * 3. Either no export ever AND data is > REMIND_AFTER_MS old,
 *    OR last export was > REMIND_AFTER_MS ago.
 * 4. Last reminder was not shown within the last SNOOZE_MS.
 */
export function shouldRemindBackup(p: BackupReminderParams): boolean {
  if (p.cloudLinked) return false;

  const hasData = p.cardStateCount >= MIN_CARD_STATES || p.deckCount >= 1;
  if (!hasData) return false;

  // Gate: don't re-nag if we already showed a reminder recently.
  if (p.lastReminderAt !== null && p.now - p.lastReminderAt < SNOOZE_MS) return false;

  if (p.lastExportAt === null) {
    // Never exported — warn only if the data is old enough (not brand-new user).
    const dataAgeRef = p.oldestDeckCreatedAt ?? p.now;
    return p.now - dataAgeRef > REMIND_AFTER_MS;
  }

  return p.now - p.lastExportAt > REMIND_AFTER_MS;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
}

export async function importBackup(
  file: File,
): Promise<{ counts: Record<string, number> }> {
  const text = await readFileAsText(file);
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Invalid JSON file");
  }

  if (
    !parsed ||
    typeof parsed !== "object" ||
    (parsed as BackupShape).version !== 1 ||
    typeof (parsed as BackupShape).dexie !== "object"
  ) {
    throw new Error("Unsupported backup format (expected version: 1)");
  }

  const backup = parsed as BackupShape;
  const counts: Record<string, number> = {};

  const tables = TABLES.map((name) => db.table(name));
  await db.transaction("rw", tables, async () => {
    for (const name of TABLES) {
      const rows = Array.isArray(backup.dexie[name]) ? backup.dexie[name] : [];
      const table = db.table(name);
      await table.clear();
      if (rows.length > 0) {
        await table.bulkPut(rows as never[]);
      }
      counts[name] = rows.length;
    }
  });

  if (backup.meta && typeof backup.meta === "object") {
    localStorage.setItem(META_KEY, JSON.stringify(backup.meta));
  } else {
    localStorage.removeItem(META_KEY);
  }

  return { counts };
}
